import { type Modification, type ModificationsMap, type ProductIngredients } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a list of modifications based on the provided ingredients and modifiers map.
 * It accumulates and updates modifications with respect to the available ingredient portions.
 */
export function generateModificationsList(
  ingredients: ProductIngredients,
  allModificationsMap: ModificationsMap,
) {
  const modificationsMap = ingredients.reduce<ModificationsMap>((map, ingredient) => {
    const ingredientId = ingredient.id;
    const modification = allModificationsMap.get(ingredientId);
    const maybeExistingModification = map.get(ingredientId);

    // ─── Invalid modification ────────────────────────────────────────────

    if (!modification) return map;

    // ─── Bump quantity if multiple portions are available ────────────────

    if (maybeExistingModification) {
      map.set(ingredientId, withOutOfStockFlag(withUpdatedQuantity(maybeExistingModification))); // prettier-ignore

      return map;
    }

    // ─── Initial portion ─────────────────────────────────────────────────

    map.set(ingredientId, withOutOfStockFlag(modification));

    return map;
  }, new Map());

  return {
    modificationsMap,
    modificationsList: [...modificationsMap.values()],
  };
}

// ─────────────────────────────────────────────────────────────────────────────

function withUpdatedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity + 1 };
}

function withOutOfStockFlag(modification: Modification): Modification {
  return { ...modification, outOfStock: modification.outOfStock };
}
